import { Dialog } from "components/Dialog";
import { API_URL_USER_PROFILE } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { makeQueryString } from "core/makeQueryString";
import { useFormik } from "formik";
import { IPostUser, User, UserType } from "interfaces/user";
import { Role } from "interfaces/userProfile";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { styled } from "@mui/material/styles";
import * as yup from "yup";
import { withTransientProps } from "core/utils/transientProps";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

const StyledDivDialogContent = styled("div", withTransientProps)<{
  $widthPx: number;
}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: ${props => props.$widthPx}px;
`;

const validationSchema = yup.object({
  firstName: yup
    .string()
    .nullable()
    .required("First Name is required"),
  lastName: yup
    .string()
    .nullable()
    .required("Last Name is required"),
  email: yup
    .string()
    .email("Enter a valid email address")
    .required("Email is required"),
  userType: yup
    .string()
    .nullable()
    .required("User Type is required"),
  roles: yup.array().label("Roles")
});

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  userType: UserType.INTERNAL,
  roles: []
};

type FormValuesType = typeof initialValues;

export interface ICreateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (u: IPostUser, resetDialog: () => void) => void;
  canCreateInternalUser: boolean;
  canSelectRoles?: boolean;
  roles?: Role[];
  loading?: boolean;
  dialogWidthPx?: number;
}

export function CreateUserDialog({
  isOpen,
  onClose,
  onConfirm,
  canCreateInternalUser,
  canSelectRoles,
  roles,
  loading,
  dialogWidthPx = 500
}: ICreateUserDialogProps): JSX.Element {
  const { axios } = useContext(AxiosContext)!;

  const [debouncedEmail, setDebouncedEmail] = useState("");

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      userType: canCreateInternalUser ? UserType.INTERNAL : UserType.EXTERNAL,
      roles: []
    },
    validationSchema,
    onSubmit: values => {
      handleSubmit(formik.isValid && !formHasErrors, values, () => {
        formik.setErrors({});
      });
    }
  });

  // Reset form when CreateUser dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { data: userExists, isLoading: loadingCheckUserExists } = useQuery<
    boolean
  >({
    queryKey: ["user-exists", debouncedEmail],
    queryFn: () =>
      debouncedEmail
        ? axios
            .get<{ count: number; results: User[] }>(
              `${API_URL_USER_PROFILE}${
                debouncedEmail ? makeQueryString({ email: debouncedEmail }) : ""
              }`
            )
            .then(r => r.data.count > 0)
        : Promise.resolve(false),
    cacheTime: 0
  });

  const formHasErrors = useMemo(() => !formik.isValid || userExists, [
    formik.isValid,
    userExists
  ]);

  const handleSubmit = useCallback(
    (isValid: boolean, values: FormValuesType, reset: () => void): void => {
      if (!isValid) return;
      onConfirm(
        {
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          user_type: values.userType as UserType,
          roles: values.roles
        },
        reset
      );
    },
    [onConfirm]
  );

  const [,] = useDebounce(() => setDebouncedEmail(formik.values.email), 600, [
    formik.values.email
  ]);

  const userTypeOptions = canCreateInternalUser
    ? [UserType.INTERNAL, UserType.EXTERNAL]
    : [UserType.EXTERNAL];

  const isLoading = loading || loadingCheckUserExists;

  return (
    <Dialog
      dialogId="dialog-create-user"
      open={isOpen}
      title="Create User"
      confirmText="Add"
      confirmDisabled={formHasErrors}
      size="large"
      actionsDisabled={isLoading}
      errorText={
        formik.submitCount > 0 && formHasErrors ? "Validation error" : undefined
      }
      onSubmit={formik.handleSubmit}
      onClose={() => {
        onClose();
        setTimeout(() => {
          formik.resetForm();
          formik.setErrors({});
        }, 50);
      }}
      aria-label="case creation dialog"
      data-qa="UM-Dialog-New-User"
      allowClickaway={false}
      fullWidth={false}
    >
      <StyledDivDialogContent $widthPx={dialogWidthPx}>
        <TextField
          variant="outlined"
          fullWidth
          name="firstName"
          label="First Name"
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
          value={formik.values.firstName}
        />
        <TextField
          variant="outlined"
          fullWidth
          name="lastName"
          label="Last Name"
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
          value={formik.values.lastName}
        />
        <TextField
          variant="outlined"
          fullWidth
          name="email"
          label="Email"
          required
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            (formik.touched.email && Boolean(formik.errors.email)) || userExists
          }
          helperText={
            (formik.touched.email && formik.errors.email) ||
            (userExists ? "A user with the same email already exists." : "")
          }
          value={formik.values.email}
        />
        <FormControl variant="outlined">
          <InputLabel id="user-type-select-label">Type</InputLabel>
          <Select
            id="userType"
            name="userType"
            label="Type"
            labelId="user-type-select-label"
            variant="outlined"
            data-testid="create-user-select-userType"
            disabled={userTypeOptions.length < 2}
            displayEmpty
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.userType}
          >
            {userTypeOptions.map(userType => (
              <MenuItem key={userType} value={userType}>
                {userType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canSelectRoles && (
          <FormControl variant="outlined">
            <InputLabel>Roles</InputLabel>
            <Select
              name="roles"
              label="Roles"
              variant="outlined"
              multiple
              value={formik.values.roles}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.roles && Boolean(formik.errors.roles)}
              disabled={loading}
            >
              {roles?.map(role => (
                <MenuItem key={role.id} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </StyledDivDialogContent>
    </Dialog>
  );
}
